import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { USER_ROUTES, ADMIN_ROUTES } from "@/routes";

/**
 * Custom hook to handle social login callback from OAuth providers
 * 
 * This hook processes URL query parameters for social login callbacks:
 * - ?token=...&user=... - Successful login
 * - ?error=... - Failed login
 * 
 * It stores the token/user in sessionStorage and navigates to the appropriate route.
 */
export function useSocialLoginCallback() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const locale = useRouteLocale();
  const currentLocale = locale || 'ro';

  useEffect(() => {
    const token = searchParams.get('token');
    const userParam = searchParams.get('user');
    const error = searchParams.get('error');
    
    if (error) {
      // Show error toast
      toast.error(t('errors.social_login_failed'));
      // Clean up URL
      navigate(`/${currentLocale}/login`, { replace: true });
      return;
    }
    
    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));
        // Store token and user in sessionStorage
        sessionStorage.setItem('access_token', decodeURIComponent(token));
        sessionStorage.setItem('user', JSON.stringify(user));
        
        // Show success message
        toast.success(t('auth.loginSuccess'));
        
        // Redirect admins to management, regular users to events
        const redirectTo = user.user_admin 
          ? ADMIN_ROUTES.management(currentLocale)
          : USER_ROUTES.events(currentLocale);
        navigate(redirectTo, { replace: true });
      } catch (e) {
        console.error('Failed to parse user data from social login:', e);
        toast.error(t('errors.social_login_failed'));
      }
    }
  }, [searchParams, navigate, currentLocale, t]);
}

